/* eslint-disable react-hooks/exhaustive-deps */
import { Box, ButtonProps, useMediaQuery, useTheme } from "@mui/material";
import { GridAddIcon, GridDeleteIcon } from "@mui/x-data-grid";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { ReactComponent as Export } from "../../assets/icons/export.svg";
import { COLORS } from "../../assets/theme";
import Actions from "../../components/common/Actions";
import CustomDateRangePicker from "../../components/common/CustomDateRangePicker";
import CustomDialog from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import SearchForm from "../../components/common/SearchForm";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import ToastNotification from "../../components/ToastNotification";
import {
  fetchProvider,
  useProviderOptionQuery,
} from "../../services/ProviderService";
import {
  fetchRoutingPlan,
  useRoutingPlansQuery,
} from "../../services/RoutingPlanService";
import {
  addSubRoutingApi,
  deleteSubRoutingApi,
  editSubRoutingApi,
  exportSubRoute,
  fetchSubRoutingHistory,
  getSubRoutingById,
  SubRouteType,
  SubRoutingCreateAllApi,
  useSubRoutingQuery,
} from "../../services/SubRoutingService";
import {
  DateRange,
  IField,
  Option,
  Params,
} from "../../types/table/interfaces.type";
import { loadOperatorsByCountry } from "../../utils/commonDataUtils";

import { fetchCountry, useCountriesQuery } from "../../services/CountryService";

import { useQueryClient } from "@tanstack/react-query";
import ConditionValuesField, {
  ConditionValue,
} from "../../components/ConditionValuesField";
import { fetchOperator } from "../../services/OperatorsService";
import History from "../../components/History";
import RelatedInstructionsDialog from "../../components/RelatedInstructionsDialog";
import AutoComplete, {
  IAutoCompleteProps,
} from "../../components/common/AutoComplete";
import { hasJwtPermission } from "../../services/ServicesHelpers";

const formatSubRoutingConditions = (
  conditionValues?: SubRouteType["conditionValues"],
) => {
  if (!conditionValues || conditionValues.length === 0) {
    return "No Conditions";
  }

  return conditionValues
    .map(
      (cv) =>
        `${cv.filterType || "Unknown"} (${cv.conditionType || "Unknown"}) : ${
          cv.value
        }`,
    )
    .join(", ");
};

const SubRoutingPage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [appliedFilters, setAppliedFilters] = useState<
    Params<SubRouteType & DateRange>
  >({
    search: "",
  });

  const { data: routingPlansOptions = [] } = useRoutingPlansQuery();
  const { data: providersOptions = [] } = useProviderOptionQuery();
  const { data: countriesOptions = [] } = useCountriesQuery();
  const RoutingPlanDetailsOptions = routingPlansOptions;
  const [operatorsByCountry, setOperatorsByCountry] = useState<Option[]>([]);
  const [isOperatorDisabled, setIsOperatorDisabled] = useState(true);
  const [filters, setFilters] = useState<
    Params<SubRouteType & { countryId: number }>
  >({});
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);

  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  useEffect(() => {
    setSelectedRowIds([]);
  }, [filters]);

  const onRowSelect = (row: SubRouteType, checked: boolean) => {
    setSelectedRowIds((prevSelected) => {
      const newSelected = checked
        ? [...prevSelected, String(row.id)]
        : prevSelected.filter((id) => id !== String(row.id));
      return newSelected;
    });
  };

  const isRowSelected = (row: SubRouteType) =>
    selectedRowIds.includes(String(row.id));

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev) => ({ ...prev, search }));
  };

  const handleSearchClick = () => {
    setAppliedFilters((prev) => ({
      ...prev,
      fromDate: fromDate,
      toDate: toDate,
      routingPlanId: filters.routingPlanId,
      operatorId: filters.operatorId,
      providerId: filters.providerId,
    }));
    setSelectedRowIds([]);
  };

  const dialogRef = useRef<SubRoutingDialogRef>(null);
  const [relatedRow, setRelatedRow] = useState<SubRouteType | null>(null);

  const handleOpenDialog = (
    mode: "add" | "edit" | "delete" | "history",
    id?: string,
    isClone = false,
  ) => {
    dialogRef.current?.openDialog(mode, id, isClone);
  };

  const actions = Actions({
    handleOpenEditModal: {
      handler: async (id: string | number) => {
        handleOpenDialog("edit", String(id));
      },
      isDisabled: !hasJwtPermission("GET_SUB_ROUTING"),
    },
    onCloneAction: {
      handler: async (id: string | number) => {
        handleOpenDialog("edit", String(id), true);
      },
      isDisabled: !hasJwtPermission("POST_SUB_ROUTING"),
    },
    onViewHistory: {
      handler: async (id: number | string) => {
        handleOpenDialog("history", String(id));
      },
      isDisabled: !hasJwtPermission("GET_ALL_SUB_ROUTINGS_HISTORY"),
    },
    onCompareRelated: {
      tooltip: "Sub Routings on the same route",
      handler: (row: SubRouteType) => setRelatedRow(row),
      isDisabled: (row: SubRouteType) =>
        !hasJwtPermission("GET_ALL_SUB_ROUTINGS") ||
        !row?.routingPlanId ||
        !row?.operatorId,
    },
  });

  const autoCompletes: IAutoCompleteProps[] = [
    {
      label: "Routing Plan",
      value: filters.routingPlanId || "",
      options: RoutingPlanDetailsOptions,
      clearable: true,
      placeholder: "Select Routing Plan",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedPlan = RoutingPlanDetailsOptions.find(
          (option) => option.value === value,
        );
        setFilters((prev) => ({
          ...prev,
          routingPlanId: String(selectedPlan?.value ?? ""),
        }));
      },
    },
    {
      label: "Country",
      value: filters.countryId ?? "",
      options: countriesOptions,
      placeholder: "Select Country",

      clearable: true,
      onChange: async (value: string | number | (string | number)[]) => {
        const selectedCountry = countriesOptions.find(
          (option) => option.value === value,
        );

        setFilters((prev) => ({
          ...prev,
          countryId: Number(selectedCountry?.value),
          operatorId: undefined,
        }));

        if (selectedCountry) {
          try {
            setIsOperatorDisabled(true);
            const operators = await loadOperatorsByCountry(
              Number(selectedCountry.value),
            );
            setOperatorsByCountry(operators);
          } catch (error: any) {
            ToastNotification.error(error.message);

            setOperatorsByCountry([]);
          } finally {
            setIsOperatorDisabled(false);
          }
        }
      },
    },
    {
      label: "Operator",
      value: filters.operatorId ?? "",
      options: operatorsByCountry,
      disabled: isOperatorDisabled,

      clearable: true,
      placeholder: "Select Operator",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedOperator = operatorsByCountry.find(
          (option) => option.value === value,
        );

        setFilters((prev) => ({
          ...prev,
          operatorId: Number(selectedOperator?.value),
        }));
      },
    },

    {
      label: "Provider",
      value: filters.providerId ?? "",
      options: providersOptions,
      clearable: true,
      placeholder: "Select Provider",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedProvider = providersOptions.find(
          (option) => option.value === value,
        );
        setFilters((prev) => ({
          ...prev,
          providerId: String(selectedProvider?.value ?? ""),
        }));
      },
    },
  ];

  const buttons = [
    {
      label: "Search",
      variant: "contained" as ButtonProps["variant"],
      bgColor: COLORS.GOLD,
      onClick: handleSearchClick,
      permission: "GET_ALL_SUB_ROUTINGS",
    },
  ];

  const fields = useMemo<IField<SubRouteType>[]>(
    () => [
      {
        key: "routingPlanId",
        header: "Routing Plan",
        transform: async (t) => {
          if (!t.routingPlanId) return "-";
          try {
            const rp = await queryClient.fetchQuery({
              queryKey: ["routingPlan", t.routingPlanId],
              queryFn: () => fetchRoutingPlan(t.routingPlanId),
            });
            return rp?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "operatorId",
        header: "Country",
        transform: async (t) => {
          if (!t.operatorId) return "-";
          try {
            const op = await queryClient.fetchQuery({
              queryKey: ["operator", t.operatorId],
              queryFn: () => fetchOperator(t.operatorId),
            });
            if (!op?.countryId) return "-";
            const country = await queryClient.fetchQuery({
              queryKey: ["country", op.countryId],
              queryFn: () => fetchCountry(op.countryId),
            });
            return country?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "operatorId",
        header: "Operator",
        transform: async (t) => {
          if (!t.operatorId) return "-";
          try {
            const op = await queryClient.fetchQuery({
              queryKey: ["operator", t.operatorId],
              queryFn: () => fetchOperator(t.operatorId),
            });
            return op?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "conditionValues",
        header: "Condition",
        transform: async (t) => formatSubRoutingConditions(t.conditionValues),
      },
      {
        key: "providerId",
        header: "Provider",
        transform: async (t) => {
          if (!t.providerId) return "-";
          try {
            const p = await queryClient.fetchQuery({
              queryKey: ["provider", t.providerId],
              queryFn: () => fetchProvider(t.providerId),
            });
            const providerName = p?.name ?? "-";
            const percentage = t.percentage0 ? ` (${t.percentage0}%)` : "";
            return `${providerName}${percentage}`;
          } catch {
            return "-";
          }
        },
      },
      { key: "createdDate", header: "Created Date" },
      { key: "createdBy", header: "User" },
    ],
    [queryClient],
  );

  const providerNameField = useCallback(
    async (providerId?: string) => {
      if (!providerId) return "-";
      try {
        const p = await queryClient.fetchQuery({
          queryKey: ["provider", providerId],
          queryFn: () => fetchProvider(providerId),
        });
        return p?.name ?? "-";
      } catch {
        return "-";
      }
    },
    [queryClient],
  );

  // Routing plan / country / operator are identical for every row of this view,
  // so only the fields that can actually differ are listed.
  const relatedFields = useMemo<IField<SubRouteType>[]>(
    () => [
      { key: "name", header: "Name" },
      { key: "position", header: "Position" },
      {
        key: "conditionValues",
        header: "Condition",
        transform: async (t) => formatSubRoutingConditions(t.conditionValues),
      },
      {
        key: "providerId",
        header: "Provider",
        transform: async (t) => providerNameField(t.providerId),
      },
      { key: "percentage0", header: "Percentage 0" },
      {
        key: "provider1Id",
        header: "Provider 1",
        transform: async (t) => providerNameField(t.provider1Id),
      },
      { key: "percentage1", header: "Percentage 1" },
      {
        key: "provider2Id",
        header: "Provider 2",
        transform: async (t) => providerNameField(t.provider2Id),
      },
      { key: "percentage2", header: "Percentage 2" },
      {
        key: "provider3Id",
        header: "Provider 3",
        transform: async (t) => providerNameField(t.provider3Id),
      },
      { key: "percentage3", header: "Percentage 3" },
      { key: "createdDate", header: "Created Date" },
      { key: "createdBy", header: "User" },
    ],
    [providerNameField],
  );

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <CustomLabel
        text="Sub Routing"
        variant={isSmallScreen ? "h6" : "h5"}
        fontWeight={500}
        marginTop={1}
        marginBottom={1}
      />
      <Box sx={{ mb: 3 }}>
        <SearchForm
          dateComponent={
            <CustomDateRangePicker
              fromDate={fromDate}
              toDate={toDate}
              setFromDate={setFromDate}
              setToDate={setToDate}
            />
          }
          autoCompletes={autoCompletes}
          buttons={buttons}
          rpdid="RPD"
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <CustomLabel
          text="Sub Routing List"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          marginTop={1}
          marginBottom={1}
        />
        <SearchHeaderContainer
          search={appliedFilters.search ?? ""}
          setSearch={handleSearchChange}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <GridAddIcon />,
              onClick: () => handleOpenDialog("add"),
              textColor: COLORS.iconColor,
              permission: "POST_SUB_ROUTING",
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              variant: "outlined" as ButtonProps["variant"],
              startIcon: <GridDeleteIcon />,
              onClick: () => handleOpenDialog("delete"),
              textColor: COLORS.iconColor,
              permission: "DELETE_SUB_ROUTING",
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <Export />,

              onClick: async () => {
                try {
                  await exportSubRoute(appliedFilters);
                  ToastNotification.success("Export completed successfully!");
                } catch (error: any) {
                  ToastNotification.error(
                    error.message || "Export failed. Please try again.",
                  );
                }
              },
              showExportProgress: true,
              textColor: COLORS.iconColor,
              label: "Export",
              permission: "GET_ALL_SUB_ROUTINGS_CSV",
            },
          ]}
        />

        <CustomTable<SubRouteType>
          filters={appliedFilters}
          fields={fields}
          fetchFunc={useSubRoutingQuery}
          actions={actions}
          checkboxSelect={true}
          onRowSelect={onRowSelect}
          isRowSelected={isRowSelected}
          sortableFields={["name", "createdDate", "createdBy"]}
        />
      </Box>
      <SubRoutingDialog
        ref={dialogRef}
        onRefresh={() => {
          setAppliedFilters((prev) => ({ ...prev }));
          queryClient.invalidateQueries({ queryKey: ["subRouting"] });
        }}
        selectedRowIds={selectedRowIds}
        onClearSelection={() => setSelectedRowIds([])}
        countriesOptions={countriesOptions}
        providersOptions={providersOptions}
        RoutingPlanDetailsOptions={RoutingPlanDetailsOptions}
        loadOperatorsByCountry={loadOperatorsByCountry}
        appliedFilters={appliedFilters}
      />
      <RelatedInstructionsDialog<SubRouteType>
        open={!!relatedRow}
        onClose={() => setRelatedRow(null)}
        row={relatedRow}
        title="Sub Routings on the Same Route"
        fields={relatedFields}
        fetchFunc={useSubRoutingQuery}
      />
    </Box>
  );
};

interface DialogData extends Omit<SubRouteType, "operatorId"> {
  operatorId?: number | number[];
  countryId: number;
}

export interface SubRoutingDialogRef {
  openDialog: (
    mode: "add" | "edit" | "delete" | "history",
    id?: string,
    isClone?: boolean,
  ) => void;
}

interface SubRoutingDialogProps {
  onRefresh: () => void;
  countriesOptions: Option[];
  RoutingPlanDetailsOptions: Option[];
  providersOptions: Option[];
  selectedRowIds: string[];
  appliedFilters: Params<SubRouteType>;

  onClearSelection: () => void;
  loadOperatorsByCountry: (countryId: number) => Promise<Option[]>;
}

const SubRoutingDialog = forwardRef<SubRoutingDialogRef, SubRoutingDialogProps>(
  (
    {
      onRefresh,
      countriesOptions,
      RoutingPlanDetailsOptions,
      providersOptions,
      appliedFilters,

      loadOperatorsByCountry,
      selectedRowIds,
      onClearSelection,
    },
    ref,
  ) => {
    const queryClient = useQueryClient();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<
      "add" | "edit" | "delete" | "history"
    >("add");
    const [isCloneMode, setIsCloneMode] = useState(false);
    const [selectedId, setSelectedId] = useState<string | undefined>();
    const [dialogData, setDialogData] = useState<Partial<DialogData>>({});
    const [operatorsByCountry, setOperatorsByCountry] = useState<Option[]>([]);
    const [isOperatorDisabled, setIsOperatorDisabled] = useState(true);
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});

    const [subLabelValue, setSubLabelValue] = useState<string[]>([""]);
    const subLabelOptions = [
      { label: "SID", value: "SENDER" },
      { label: "Content", value: "CONTENT" },
      { label: "Receiver", value: "RECEIVER" },
      { label: "IP", value: "IP" },
    ];

    const onSubLabelChange = (index: number, value: string) => {
      setSubLabelValue((prev) => {
        const updated = [...prev];
        updated[index] = value;
        return updated;
      });
    };

    const validateField = (field: string, value: string) => {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: !value,
      }));
    };

    useImperativeHandle(ref, () => ({
      openDialog: (mode, id, isClone = false) => {
        setDialogMode(isClone ? "add" : mode);
        setSelectedId(isClone ? undefined : id);
        setIsCloneMode(isClone);
        openDialog(mode, id, isClone);
        setDialogOpen(true);
      },
    }));
    useEffect(() => {
      const requiredFields = [
        "routingPlanId",
        "countryId",
        "operatorId",
        "providerId",
        "name",
        "conditionValues",
      ];

      const isAnyFieldEmpty = requiredFields.some((field) => {
        const value = dialogData[field as keyof typeof dialogData];
        if (Array.isArray(value)) return value.length === 0;
        return value === undefined || value === null || value === "";
      });

      setIsSaveDisabled(isAnyFieldEmpty);
    }, [dialogData]);

    const resetDialogData = () => {
      setDialogData({});
      setDialogOpen(false);
      setOperatorsByCountry([]);
      setIsSaveDisabled(true);
      setIsOperatorDisabled(true);
      setIsCloneMode(false);
    };

    const handleCountryChange = async (
      value: string | number | (string | number)[],
    ) => {
      const selectedCountry = Array.isArray(value) ? value[0] : value;
      setDialogData((prev) => ({
        ...prev,
        countryId: Number(selectedCountry),
        operatorId: undefined,
      }));

      if (selectedCountry) {
        try {
          const countryId = Number(selectedCountry);

          setIsOperatorDisabled(false);
          const operators = await loadOperatorsByCountry(countryId);
          setOperatorsByCountry(operators);
        } catch (error: any) {
          ToastNotification.error(
            "Error fetching operators for selected country.",
          );
          setOperatorsByCountry([]);
        }
      } else {
        setOperatorsByCountry([]);
        setIsOperatorDisabled(true);
      }
    };

    const validateForm = () => {
      const errors = {
        name: !dialogData.name,
        routingPlanId: !dialogData.routingPlanId,
        operatorId: !dialogData.operatorId,
        providerId: !dialogData.providerId,
        position: !dialogData.position,
      };
      setIsSaveDisabled(Object.values(errors).some(Boolean));
    };

    useEffect(validateForm, [dialogData]);

    const openDialog = async (
      mode: "add" | "edit" | "delete" | "history",
      id?: string,
      isClone = false,
    ) => {
      setDialogMode(isClone ? "add" : mode);

      try {
        if (mode === "edit" || isClone) {
          if (!id) {
            console.warn("ID is required to edit a Sub routing.");
            ToastNotification.warning("Sub routing ID is required.");
            return;
          }

          const subRoutingData = await getSubRoutingById(id);

          const processedConditionValues = subRoutingData.conditionValues?.map(
            (condition: Partial<ConditionValue>) => ({
              conditionType: condition.conditionType ?? "CONTAINS",
              value: condition.value ?? "",
              filterType: condition.filterType ?? "",
            }),
          ) || [{ conditionType: "CONTAINS", value: "", filterType: "" }];

          setConditionStatuses(processedConditionValues);
          setSubLabelValue(
            processedConditionValues.map(
              (c: ConditionValue) => c.filterType || "",
            ),
          );

          // A clone keeps every value of the source route, only the name has to
          // be re-entered because it must stay unique.
          let countryId: number | undefined;

          if (subRoutingData.operatorId) {
            const { countryId: selectedCountry } = await fetchOperator(
              subRoutingData.operatorId,
            );

            if (selectedCountry) {
              countryId = Number(selectedCountry);

              setIsOperatorDisabled(true);
              const operators = await loadOperatorsByCountry(countryId);
              setOperatorsByCountry(operators);
              setIsOperatorDisabled(false);
            }
          }

          const sourceOperatorId = Array.isArray(subRoutingData.operatorId)
            ? subRoutingData.operatorId[0]
            : subRoutingData.operatorId;

          setDialogData({
            ...subRoutingData,
            ...(isClone ? { id: undefined, version: undefined, name: "" } : {}),
            conditionValues: processedConditionValues,
            countryId: countryId ?? "",
            operatorId:
              isClone && sourceOperatorId != null
                ? [Number(sourceOperatorId)]
                : sourceOperatorId,
          });
        } else if (mode === "delete" && id) {
          setDialogData({ id });
        } else if (mode === "add") {
          setDialogData({
            name: "",
            conditionValues: [
              { filterType: "", conditionType: "CONTAINS", value: "" },
            ],
            effectiveDate: new Date(),
          });

          setConditionStatuses([{ conditionType: "", value: "" }]);
          setSubLabelValue([""]);
        }

        setDialogOpen(true);
      } catch (error: any) {
        console.error(
          `Error fetching Sub routing data for ${mode} mode:`,
          error,
        );
        ToastNotification.error("Failed to fetch Sub routing data.");
      }
    };

    const addSubRouting = async (data: Partial<DialogData>) => {
      try {
        const { operatorId, ...rest } = data;

        const filteredData = Object.entries(rest).reduce(
          (acc, [key, value]) => {
            if (value !== undefined && value !== null && value !== "") {
              acc[key] = value;
            }
            return acc;
          },
          {} as Record<string, any>,
        );

        const commonData: Partial<SubRouteType> = {
          ...filteredData,
          type: "SUB_ROUTING",
          isDeleted: false,
          conditionValues: conditionStatuses.length
            ? conditionStatuses.map((condition, idx) => ({
                filterType: subLabelValue[idx] || "",
                conditionType: condition.conditionType ?? "CONTAINS",
                value: condition.value != null ? String(condition.value) : "",
              }))
            : [{ filterType: "", conditionType: "CONTAINS", value: "" }],
          effectiveDate: new Date(),
        };

        if (Array.isArray(operatorId) && operatorId.length >= 1) {
          const payloads: Partial<SubRouteType>[] = operatorId.map(
            (id, index) => ({
              ...commonData,
              operatorId: id,
              name: data.name ? `${data.name}_${index + 1}` : undefined,
              routingPlanId: data.routingPlanId,
              providerId: data.providerId,
            }),
          );

          await SubRoutingCreateAllApi(payloads);
        }

        onRefresh();
        setConditionStatuses([{ conditionType: "", value: "" }]);
        setDialogOpen(false);
        setDialogData({});
        ToastNotification.success("Sub routing added successfully!");
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const editSubRouting = async (data: Partial<DialogData>) => {
      try {
        const id = data.id as string;
        if (!id) {
          ToastNotification.warning("Sub routing ID is required.");
          return;
        }
        const { operatorId, percentage0, ...rest } = data;

        const filteredData = Object.entries(rest).reduce(
          (acc, [key, value]) => {
            if (value !== undefined && value !== null && value !== "") {
              acc[key] = value;
            }
            return acc;
          },
          {} as Record<string, any>,
        );

        const commonData: Partial<SubRouteType> = {
          ...filteredData,
          type: "SUB_ROUTING",
          isDeleted: false,
          conditionValues: conditionStatuses.length
            ? conditionStatuses.map((condition, idx) => ({
                filterType: subLabelValue[idx] || "",
                conditionType: condition.conditionType ?? "CONTAINS",
                value: condition.value != null ? String(condition.value) : "",
              }))
            : [{ filterType: "", conditionType: "CONTAINS", value: "" }],
        };

        const payload: Partial<SubRouteType> = {
          ...commonData,
          operatorId: Array.isArray(operatorId) ? operatorId[0] : operatorId,
          name: data.name,
          routingPlanId: data.routingPlanId,
          providerId: data.providerId,
        };

        await editSubRoutingApi(id, payload);

        onRefresh();
        setConditionStatuses([{ conditionType: "", value: "" }]);
        setDialogOpen(false);
        setDialogData({});
        ToastNotification.success("Sub routing updated successfully!");
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };
    const deleteSubRouting = async () => {
      try {
        await deleteSubRoutingApi(selectedRowIds);
        ToastNotification.success("Sub routing deleted successfully");
        onClearSelection();
        onRefresh();
        setDialogOpen(false);
      } catch {
        ToastNotification.error("Error deleting Sub routing.");
      }
    };

    const stringErrors = Object.keys(errors).reduce<Record<string, string>>(
      (acc, key) => {
        acc[key] = errors[key] ? "This field is required" : "";
        return acc;
      },
      {},
    );

    const [conditionStatuses, setConditionStatuses] = useState<
      ConditionValue[]
    >([{ conditionType: "", value: "" }]);

    const handleConditionChange = (
      index: number,
      field: string,
      value: string,
    ) => {
      setConditionStatuses((prev) => {
        const updatedConditions = [...prev];
        updatedConditions[index] = {
          ...updatedConditions[index],
          [field]: value,
        };
        return updatedConditions;
      });
    };

    const handleAddCondition = () => {
      setConditionStatuses((prev) => [
        ...prev,
        { conditionType: "", value: "" },
      ]);
      setSubLabelValue((prev) => [...prev, ""]);
    };

    const handleRemoveCondition = (index: number) => {
      setConditionStatuses((prev) => {
        const updatedConditions = [...prev];
        updatedConditions.splice(index, 1);
        return updatedConditions;
      });
      setSubLabelValue((prev) => {
        const updated = [...prev];
        updated.splice(index, 1);
        return updated;
      });
    };

    const fieldHistory = useMemo<
      IField<SubRouteType & { historyTimestamp: Date }>[]
    >(
      () => [
        {
          key: "name",
          header: "Sub Route Name",
        },
        {
          key: "routingPlanId",
          header: "Routing Plan",
          transform: async (t) => {
            if (!t.routingPlanId) return "-";
            try {
              const rp = await queryClient.fetchQuery({
                queryKey: ["routingPlan", t.routingPlanId],
                queryFn: () => fetchRoutingPlan(t.routingPlanId),
              });
              return rp?.name ?? "-";
            } catch {
              return "-";
            }
          },
        },
        {
          key: "operatorId",
          header: "Country",
          transform: async (t) => {
            if (!t.operatorId) return "-";
            try {
              const op = await queryClient.fetchQuery({
                queryKey: ["operator", t.operatorId],
                queryFn: () => fetchOperator(t.operatorId),
              });
              if (!op?.countryId) return "-";
              const country = await queryClient.fetchQuery({
                queryKey: ["country", op.countryId],
                queryFn: () => fetchCountry(op.countryId),
              });
              return country?.name ?? "-";
            } catch {
              return "-";
            }
          },
        },
        {
          key: "operatorId",
          header: "Operator",
          transform: async (t) => {
            if (!t.operatorId) return "-";
            try {
              const op = await queryClient.fetchQuery({
                queryKey: ["operator", t.operatorId],
                queryFn: () => fetchOperator(t.operatorId),
              });
              return op?.name ?? "-";
            } catch {
              return "-";
            }
          },
        },
        {
          key: "conditionValues",
          header: "Condition",
          transform: async (t) => formatSubRoutingConditions(t.conditionValues),
        },
        {
          key: "providerId",
          header: "Provider",
          transform: async (t) => {
            if (!t.providerId) return "-";
            try {
              const p = await queryClient.fetchQuery({
                queryKey: ["provider", t.providerId],
                queryFn: () => fetchProvider(t.providerId),
              });
              const providerName = p?.name ?? "-";
              const percentage = t.percentage0 ? ` (${t.percentage0}%)` : "";
              return `${providerName}${percentage}`;
            } catch {
              return "-";
            }
          },
        },
        {
          key: "provider1Id",
          header: "Provider 1",
          transform: async (t) => {
            if (!t.provider1Id) return "-";
            try {
              const p = await queryClient.fetchQuery({
                queryKey: ["provider", t.provider1Id],
                queryFn: () => fetchProvider(t.provider1Id),
              });
              const providerName = p?.name ?? "-";
              const percentage = t.percentage1 ? ` (${t.percentage1}%)` : "";
              return `${providerName}${percentage}`;
            } catch {
              return "-";
            }
          },
        },
        {
          key: "provider2Id",
          header: "Provider 2",
          transform: async (t) => {
            if (!t.provider2Id) return "-";
            try {
              const p = await queryClient.fetchQuery({
                queryKey: ["provider", t.provider2Id],
                queryFn: () => fetchProvider(t.provider2Id),
              });
              const providerName = p?.name ?? "-";
              const percentage = t.percentage2 ? ` (${t.percentage2}%)` : "";
              return `${providerName}${percentage}`;
            } catch {
              return "-";
            }
          },
        },
        {
          key: "provider3Id",
          header: "Provider 3",
          transform: async (t) => {
            if (!t.provider3Id) return "-";
            try {
              const p = await queryClient.fetchQuery({
                queryKey: ["provider", t.provider3Id],
                queryFn: () => fetchProvider(t.provider3Id),
              });
              const providerName = p?.name ?? "-";
              const percentage = t.percentage3 ? ` (${t.percentage3}%)` : "";
              return `${providerName}${percentage}`;
            } catch {
              return "-";
            }
          },
        },
        { key: "createdDate", header: "Created Date" },
        { key: "createdBy", header: "User" },
        { key: "historyTimestamp", header: "Date Modified" },
        { key: "createdBy", header: "Created By" },
        { key: "updatedBy", header: "Updated By" },
      ],
      [queryClient],
    );

    return (
      <CustomDialog
        open={dialogOpen}
        onClose={resetDialogData}
        mode={dialogMode}
        title={
          dialogMode === "history"
            ? "History"
            : dialogMode === "delete"
              ? "Delete Sub Routing"
              : dialogMode === "edit"
                ? "Edit Sub Routing"
                : isCloneMode
                  ? "Clone Sub Routing"
                  : "Add New Sub Routing"
        }
        fields={
          dialogMode === "history"
            ? [
                {
                  type: "custom",
                  component: (
                    <History
                      filters={appliedFilters}
                      fields={fieldHistory}
                      fetchFunc={async (params: any) => {
                        const size = params?.size ?? 10;
                        const requestParams = Object.fromEntries(
                          Object.entries({
                            ...params,
                            size,
                            sort: "historyTimestamp,desc",
                          }).filter(([, value]) => {
                            if (value === null || value === undefined) {
                              return false;
                            }

                            if (typeof value === "string") {
                              return value.trim() !== "";
                            }

                            return true;
                          }),
                        );
                        const result = await fetchSubRoutingHistory(
                          String(selectedId),
                          requestParams,
                        );
                        return {
                          ...result,
                          content: result.content.map((item: SubRouteType) => ({
                            ...item,
                            historyTimestamp:
                              (item as any).historyTimestamp ?? new Date(),
                          })),
                        };
                      }}
                    />
                  ),
                },
              ]
            : dialogMode === "delete"
              ? [
                  {
                    type: "custom",
                    component: (
                      <Box>
                        <p>Are you sure you want to delete this route?</p>
                      </Box>
                    ),
                  },
                ]
              : [
                  {
                    type: "text",
                    label: "Sub Routing Name",
                    bgColor: COLORS.WhiteBackground,
                    highlightLabelStar: true,
                    value: dialogData.name || "",
                    validateOnChange: true,
                    onValidation: (isError) => {
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        name: isError,
                      }));
                    },
                    onChange: (value) => {
                      const trimmedValue = String(value).trimStart();
                      setDialogData((prev) => ({
                        ...prev,
                        name: trimmedValue,
                      }));
                    },
                    placeholder: "Session name",
                    error: !!errors.name,
                  },

                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Routing Plan"
                        value={dialogData.routingPlanId ?? ""}
                        options={RoutingPlanDetailsOptions}
                        onChange={(
                          value: string | number | (string | number)[],
                        ) => {
                          setDialogData((prev) => ({
                            ...prev,
                            routingPlanId: String(value),
                          }));
                          validateField("routingPlan", String(value));
                        }}
                        clearable={true}
                        disablePortal={false}
                        error={!!errors.routingPlan}
                        placeholder="Select Routing Plan"
                        highlightLabelStar={true}
                        helperText={
                          errors.routingPlan ? "Routing Plan is required" : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Country"
                        value={dialogData.countryId ?? ""}
                        options={countriesOptions}
                        onChange={handleCountryChange}
                        clearable={true}
                        disablePortal={false}
                        error={!!errors.country}
                        placeholder="Select Country"
                        highlightLabelStar={true}
                        helperText={errors.country ? "Country is required" : ""}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Operator"
                        multiple={dialogMode === "add"}
                        selectAll={dialogMode === "add"}
                        hideChips={dialogMode === "add"}
                        value={
                          dialogMode === "add"
                            ? Array.isArray(dialogData.operatorId)
                              ? dialogData.operatorId.map(String)
                              : []
                            : dialogData.operatorId !== undefined &&
                                dialogData.operatorId !== null
                              ? String(
                                  Array.isArray(dialogData.operatorId)
                                    ? dialogData.operatorId[0]
                                    : dialogData.operatorId,
                                )
                              : ""
                        }
                        options={operatorsByCountry}
                        onChange={(
                          value: string | number | (string | number)[],
                        ) => {
                          if (dialogMode === "add") {
                            const valueArray: number[] = Array.isArray(value)
                              ? value.map((v) => Number(v))
                              : value
                                ? [Number(value)]
                                : [];

                            setDialogData((prev) => ({
                              ...prev,
                              operatorId: valueArray,
                            }));
                          } else {
                            setDialogData((prev) => ({
                              ...prev,
                              operatorId:
                                value !== "" && value !== undefined
                                  ? Number(
                                      Array.isArray(value) ? value[0] : value,
                                    )
                                  : undefined,
                            }));
                          }
                        }}
                        clearable={true}
                        disablePortal={false}
                        error={!!errors.operatorId}
                        placeholder="Select Operator"
                        highlightLabelStar={true}
                        helperText={
                          errors.operatorId ? "Operator is required" : ""
                        }
                        disabled={isOperatorDisabled}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "select",
                    label: "Position",
                    bgColor: COLORS.WhiteBackground,
                    highlightLabelStar: true,
                    value: dialogData.position || "",
                    options: [
                      {
                        label: "Before TPS Routing",
                        value: "BEFORE_TPS_ROUTING",
                      },
                      {
                        label: "After TPS Routing",
                        value: "AFTER_TPS_ROUTING",
                      },
                    ],
                    onChange: (
                      value: string | number | (string | number)[],
                    ) => {
                      setDialogData((prev) => ({
                        ...prev,
                        position: String(value) as
                          | "BEFORE_TPS_ROUTING"
                          | "AFTER_TPS_ROUTING",
                      }));
                    },
                    placeholder: "Select Position",
                    error: !!errors.position,
                    helperText: errors.position ? "Position is required" : "",
                  },
                  {
                    type: "custom",
                    label: "Conditions",
                    highlightLabelStar: true,
                    component: (
                      <ConditionValuesField
                        type="text"
                        label="Sender ID"
                        sub
                        fallback
                        showCaseSensitivityNote
                        conditionTypeOptions={[
                          { label: "Equals", value: "EQUALS" },
                          { label: "ALFA", value: "ALFA" },
                          { label: "NUMERIC", value: "NUMERIC" },
                          { label: "ALPHANUMERIC", value: "ALPHANUMERIC" },
                          { label: "Contains", value: "CONTAINS" },
                          { label: "URL", value: "URL" },
                          { label: "Starts With", value: "STARTS_WITH" },
                          { label: "List", value: "LIST" },
                        ]}
                        conditionStatuses={conditionStatuses}
                        errors={stringErrors}
                        handleConditionChange={handleConditionChange}
                        handleAddCondition={handleAddCondition}
                        handleRemoveCondition={handleRemoveCondition}
                        subLabelOptions={subLabelOptions}
                        subLabelValue={subLabelValue}
                        onSubLabelChange={onSubLabelChange}
                      />
                    ),
                  },
                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Provider"
                        value={dialogData.providerId ?? ""}
                        options={providersOptions}
                        onChange={(
                          value: string | number | (string | number)[],
                        ) => {
                          setDialogData((prev) => ({
                            ...prev,
                            providerId: String(value),
                          }));
                          validateField("providerId", String(value));
                        }}
                        clearable={true}
                        disablePortal={false}
                        error={!!errors.providerId}
                        placeholder="Select Provider"
                        highlightLabelStar={true}
                        helperText={
                          errors.providerId ? "Provider is required" : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "combined",
                    label: "Blending",
                    showLabel: true,
                    fields: [
                      {
                        type: "custom",
                        component: (
                          <AutoComplete
                            value={dialogData.provider1Id ?? ""}
                            options={providersOptions}
                            onChange={(
                              value: string | number | (string | number)[],
                            ) => {
                              setDialogData((prev) => ({
                                ...prev,
                                provider1Id: Array.isArray(value)
                                  ? value.join(",")
                                  : String(value),
                              }));
                            }}
                            clearable={true}
                            disablePortal={false}
                            error={!!errors.provider1Id}
                            placeholder="Provider 1"
                            highlightLabelStar={true}
                            helperText={
                              errors.provider1Id ? "Provider is required" : ""
                            }
                            width="auto"
                            sx={{
                              flex: 1,
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: COLORS.WhiteBackground,
                              },
                            }}
                          />
                        ),
                      },
                      {
                        type: "text",
                        value: dialogData.percentage1 || "",
                        placeholder: "Percentage 1",
                        bgColor: COLORS.WhiteBackground,
                        onChange: (value: string | number) => {
                          const numericValue = Number(value);
                          const cappedValue =
                            numericValue > 100 ? 100 : numericValue;
                          setDialogData((prev) => ({
                            ...prev,
                            percentage1: cappedValue,
                          }));
                        },
                        error: !!errors.percentage1,
                        helperText: errors.percentage1
                          ? "Percentage 1 is required"
                          : "",
                      },
                    ],
                    layout: "row",
                    sx: { gap: 3 },
                    highlightLabelStar: true,
                  },
                  {
                    type: "combined",
                    fields: [
                      {
                        type: "custom",
                        component: (
                          <AutoComplete
                            value={dialogData.provider2Id ?? ""}
                            options={providersOptions}
                            onChange={(
                              value: string | number | (string | number)[],
                            ) => {
                              setDialogData((prev) => ({
                                ...prev,
                                provider2Id: Array.isArray(value)
                                  ? value.join(",")
                                  : String(value),
                              }));
                            }}
                            clearable={true}
                            disablePortal={false}
                            error={!!errors.provider2Id}
                            placeholder="Provider 2"
                            highlightLabelStar={true}
                            helperText={
                              errors.provider2Id ? "Provider is required" : ""
                            }
                            width="auto"
                            sx={{
                              flex: 1,
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: COLORS.WhiteBackground,
                              },
                            }}
                          />
                        ),
                      },
                      {
                        type: "text",
                        value: dialogData.percentage2 || "",
                        placeholder: "Percentage 2",
                        bgColor: COLORS.WhiteBackground,
                        onChange: (value: string | number) => {
                          const numericValue = Number(value);
                          const cappedValue =
                            numericValue > 100 ? 100 : numericValue;
                          setDialogData((prev) => ({
                            ...prev,
                            percentage2: cappedValue,
                          }));
                        },
                        error: !!errors.percentage2,
                        helperText: errors.percentage2
                          ? "Percentage 2 is required"
                          : "",
                      },
                    ],
                    layout: "row",
                    sx: { gap: 3 },
                    highlightLabelStar: true,
                  },
                  {
                    type: "combined",
                    fields: [
                      {
                        type: "custom",
                        component: (
                          <AutoComplete
                            value={dialogData.provider3Id ?? ""}
                            options={providersOptions}
                            onChange={(
                              value: string | number | (string | number)[],
                            ) => {
                              setDialogData((prev) => ({
                                ...prev,
                                provider3Id: Array.isArray(value)
                                  ? value.join(",")
                                  : String(value),
                              }));
                            }}
                            clearable={true}
                            disablePortal={false}
                            error={!!errors.provider3Id}
                            placeholder="Provider 3"
                            highlightLabelStar={true}
                            helperText={
                              errors.provider3Id ? "Provider is required" : ""
                            }
                            width="auto"
                            sx={{
                              flex: 1,
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: COLORS.WhiteBackground,
                              },
                            }}
                          />
                        ),
                      },
                      {
                        type: "text",
                        value: dialogData.percentage3 || "",
                        placeholder: "Percentage 3",
                        bgColor: COLORS.WhiteBackground,
                        onChange: (value: string | number) => {
                          const numericValue = Number(value);
                          const cappedValue =
                            numericValue > 100 ? 100 : numericValue;
                          setDialogData((prev) => ({
                            ...prev,
                            percentage3: cappedValue,
                          }));
                        },
                        error: !!errors.percentage3,
                        helperText: errors.percentage3
                          ? "Percentage 3 is required"
                          : "",
                      },
                    ],
                    layout: "row",
                    sx: { gap: 3 },
                    highlightLabelStar: true,
                  },
                ]
        }
        buttons={
          dialogMode === "history"
            ? []
            : [
                dialogMode !== "delete"
                  ? {
                      label: "Save",
                      variant: "contained",
                      bgColor: COLORS.GOLD,
                      borderRadius: "12px",
                      onClick: async () => {
                        if (dialogMode === "edit") {
                          await editSubRouting(dialogData);
                        } else {
                          await addSubRouting(dialogData);
                        }
                      },
                      disabled:
                        isSaveDisabled ||
                        (dialogMode === "edit"
                          ? !hasJwtPermission("PUT_SUB_ROUTING")
                          : !hasJwtPermission("POST_SUB_ROUTING")),
                    }
                  : {
                      label: "Delete",
                      variant: "contained",
                      bgColor: COLORS.GOLD,
                      textColor: COLORS.iconColor,
                      borderRadius: "12px",
                      onClick: deleteSubRouting,
                      disabled: !hasJwtPermission("DELETE_SUB_ROUTING"),
                    },
              ]
        }
      />
    );
  },
);

export default SubRoutingPage;
